import React, { useState } from "react";

import useOrderTracking from "../../../hooks/useOrderTracking";
import {
  buildTimeline,
  canShowEdit,
  dateSlotLine,
  formatMoney,
  knownDriverName,
  liveTrackingView,
  orderIssue,
  orderSummary,
  paymentIssue,
  placeLine,
  statusConfig,
  trackingEnabled,
} from "../../../utils/orders";
import { Icon } from "./icons";
import LiveMap from "./LiveMap";

/**
 * Une commande en cours — design « OrderBlock.dc.html ».
 *
 * Un créneau manqué ou un paiement échoué remplace tout le bloc par sa carte
 * problème, comme sur mobile. Sinon : carte statut (avec le suivi en direct
 * les jours de collecte ou de livraison), détail dépliable, lien de
 * modification, et la frise à droite.
 */
export default function OrderBlock({
  order,
  t,
  lang,
  laundryName,
  authUser,
  homeAddresses,
  officeAddresses,
  walletBalance,
  cards,
  onEdit,
  onResolve,
  onTryAnotherCard,
  onTopup,
}) {
  const tracking = useOrderTracking(order.id, trackingEnabled(order), authUser);
  const live = liveTrackingView(tracking.data, { failed: tracking.failed }, t);
  const issue = orderIssue(order);

  if (issue === "pickup" || issue === "delivery") {
    return <MissedSlotCard order={order} leg={issue} live={live} t={t} lang={lang} onResolve={onResolve} />;
  }

  if (issue === "payment") {
    return (
      <PaymentIssueCard
        order={order}
        info={paymentIssue(order, { walletBalance, cards }, t)}
        t={t}
        onTryAnotherCard={onTryAnotherCard}
        onTopup={onTopup}
      />
    );
  }

  const context = { t, lang, laundryName, live };

  return (
    <div className="wosh-ord-block">
      <div className="wosh-ord-block__main">
        <StatusHero order={order} context={context} diag={tracking.diag} />
        <OrderDetail
          order={order}
          t={t}
          summary={orderSummary(order, { lang, homeAddresses, officeAddresses })}
        />
        {canShowEdit(order) && (
          <button type="button" className="wosh-ord-edit" onClick={() => onEdit(order)}>
            {t("orders.editOrder")}
            <Icon name="arrow-forward" />
          </button>
        )}
      </div>
      <Timeline steps={buildTimeline(order, context)} t={t} />
    </div>
  );
}

/* ── Carte statut ──────────────────────────────────────── */

function StatusHero({ order, context, diag }) {
  const { t, live } = context;
  const config = statusConfig(order, context);
  const place = placeLine(order);
  const [showDiag, setShowDiag] = useState(false);

  /*
   * Aide au support, comme l'appui long du mobile : un clic droit sur le
   * titre montre ce que le suivi a répondu en dernier. Rien n'est envoyé.
   */
  const toggleDiag = (event) => {
    event.preventDefault();
    setShowDiag((value) => !value);
  };

  const diagLine = !diag
    ? `order=${order.id} · ${trackingEnabled(order) ? "aucune réponse" : "suivi inactif (pas le jour J)"}`
    : diag.ok
    ? `order=${order.id} · ${diag.at.slice(11, 19)}Z · http=${diag.http} · state=${diag.state}/${diag.leg ?? "-"} · stops=${diag.stops ?? "-"} · driver=${diag.driver ? "yes" : "no"}`
    : `order=${order.id} · ${diag.at.slice(11, 19)}Z · ÉCHEC http=${diag.http ?? "-"}`;

  return (
    <section className="wosh-ord-hero">
      <div className="wosh-ord-hero__head">
        <div className="wosh-ord-hero__status">
          <span className="wosh-ord-pulse" />
          <span className="wosh-ord-hero__eyebrow">{config.eyebrow}</span>
        </div>
        {/* « Plein écran » ouvre la carte en direct (/orders/:id/tracking),
            qui n'existe pas encore sur le web : le bouton viendra avec elle. */}
      </div>
      <div className="wosh-ord-hero__title" onContextMenu={toggleDiag}>
        {config.title}
      </div>
      {config.sub && <div className="wosh-ord-hero__sub">{config.sub}</div>}
      {showDiag && <div className="wosh-ord-hero__diag">{diagLine}</div>}
      {place && (
        <div className="wosh-ord-hero__place">
          <Icon name="location-outline" />
          <span>{place}</span>
        </div>
      )}
      <LiveSection live={live} t={t} />
    </section>
  );
}

/**
 * Le suivi dans la carte statut. À l'approche : ligne verte, carte (ou
 * position en cours d'acquisition) et chauffeur en direct. Avant et après :
 * une ligne chauffeur, seulement quand on connaît son nom.
 */
function LiveSection({ live, t }) {
  if (!live.isActive) return null;

  if (live.phase === "approaching") {
    return (
      <>
        <div className="wosh-ord-hero__divider" />
        <div className="wosh-ord-approach">
          <div className="wosh-ord-approach__line">
            <span className="wosh-ord-pulse wosh-ord-pulse--live" />
            {live.copy.sub}
          </div>
          {live.position ? (
            <LiveMap position={live.position} destination={live.destination} />
          ) : (
            <div className="wosh-ord-approach__acquiring">
              <span className="wosh-ord-approach__spinner" />
              {t("liveTracking.acquiringPosition")}
            </div>
          )}
          {live.driverName && (
            <div className="wosh-ord-driver">
              <div className="wosh-ord-driver__avatar">
                {live.driverName.charAt(0).toUpperCase()}
                <span className="wosh-ord-driver__avatar-dot" />
              </div>
              <div className="wosh-ord-driver__text">
                <div className="wosh-ord-driver__name">{live.driverName}</div>
                <div className="wosh-ord-driver__hint">{t("liveTracking.ring")}</div>
              </div>
              <div className="wosh-ord-driver__live">LIVE</div>
            </div>
          )}
        </div>
      </>
    );
  }

  if (!live.driverName) return null;

  const assigned = live.phase === "pending";

  return (
    <>
      <div className="wosh-ord-hero__divider" />
      <div className="wosh-ord-live">
        <div className="wosh-ord-live__tile">
          <Icon name={live.phase === "completed" ? "checkmark-done-outline" : "bus-outline"} />
          <span className="wosh-ord-live__tile-dot" />
        </div>
        <div className="wosh-ord-live__text">
          {assigned && <div className="wosh-ord-live__eyebrow">{live.copy.eyebrow}</div>}
          <div className="wosh-ord-live__title">{live.copy.title}</div>
          {live.copy.sub && <div className="wosh-ord-live__sub">{live.copy.sub}</div>}
        </div>
      </div>
    </>
  );
}

/* ── Détail de la commande ─────────────────────────────── */

function OrderDetail({ order, summary, t }) {
  const [open, setOpen] = useState(false);
  if (!summary.items.length) return null;

  return (
    <div className="wosh-ord-card">
      <button
        type="button"
        className="wosh-ord-acc__toggle"
        aria-expanded={open}
        aria-controls={`wosh-ord-detail-${order.id}`}
        onClick={() => setOpen((value) => !value)}
      >
        {t(open ? "orders.hideOrderDetail" : "orders.showOrderDetail")}
        <Icon name={open ? "chevron-up" : "chevron-down"} />
      </button>
      {open && (
        <div className="wosh-ord-acc__body" id={`wosh-ord-detail-${order.id}`}>
          {summary.items.map((item) => (
            <div className="wosh-ord-acc__line" key={item.id}>
              <span className="wosh-ord-acc__qty">{item.qty}</span>
              <span className="wosh-ord-acc__name">{item.name}</span>
              {item.total !== null && <span className="wosh-ord-acc__price">{formatMoney(item.total)}</span>}
            </div>
          ))}
          {summary.fee && (
            <div className="wosh-ord-acc__row">
              <span>{t(summary.fee.kind === "service" ? "checkout.serviceFee" : "checkout.deliveryFee")}</span>
              <strong>{formatMoney(summary.fee.amount)}</strong>
            </div>
          )}
          {summary.promo && (
            <div className="wosh-ord-acc__row wosh-ord-acc__promo">
              <span>
                <Icon name="pricetag-outline" />
                {t("orders.promoApplied", { code: summary.promo.code })}
              </span>
              {summary.promo.discount !== null && <span>{formatMoney(-summary.promo.discount)}</span>}
            </div>
          )}
          {summary.total !== null && (
            <>
              <div className="wosh-ord-acc__rule" />
              <div className="wosh-ord-acc__total">
                <span>{t("orders.orderTotal")}</span>
                <span>{formatMoney(summary.total)}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Frise ─────────────────────────────────────────────── */

function Timeline({ steps, t }) {
  return (
    <section className="wosh-ord-tl">
      <div className="wosh-ord-tl__eyebrow">{t("orders.timelineEyebrow")}</div>
      {steps.map((step, index) => {
        const look = step.state === "active" && step.highlight ? "highlight" : step.state;
        return (
          <div className={`wosh-ord-tl__step wosh-ord-tl__step--${look}`} key={step.id}>
            <div className="wosh-ord-tl__rail">
              <div className={`wosh-ord-tl__dot wosh-ord-tl__dot--${look}`}>
                {step.state === "done" && <Icon name="checkmark-sharp" />}
              </div>
              {index < steps.length - 1 && (
                <div className={`wosh-ord-tl__line${step.state === "done" ? " wosh-ord-tl__line--done" : ""}`} />
              )}
            </div>
            <div className="wosh-ord-tl__body">
              <div className="wosh-ord-tl__title">{step.title}</div>
              {step.meta && <div className="wosh-ord-tl__meta">{step.meta}</div>}
              {step.detail && (
                <div className={`wosh-ord-tl__detail${step.warn ? " wosh-ord-tl__detail--warn" : ""}`}>
                  {step.detail}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}

/* ── Cartes problème ───────────────────────────────────── */

function IssueCard({ icon, eyebrow, sub, title, desc, children }) {
  return (
    <section className="wosh-ord-issue">
      <div className="wosh-ord-issue__head">
        <div className="wosh-ord-issue__badge">
          <Icon name={icon} />
        </div>
        <div className="wosh-ord-issue__caps">
          <div className="wosh-ord-issue__eyebrow">{eyebrow}</div>
          {sub && <div className="wosh-ord-issue__sub">{sub}</div>}
        </div>
      </div>
      <div className="wosh-ord-issue__text">
        <div className="wosh-ord-issue__title">{title}</div>
        <div className="wosh-ord-issue__desc">{desc}</div>
      </div>
      <div className="wosh-ord-issue__actions">{children}</div>
    </section>
  );
}

export function MissedSlotCard({ order, leg, live, t, lang, onResolve }) {
  const pickup = leg === "pickup";
  const name = knownDriverName(order, live) || t("home.driverFallback");
  const slot = pickup
    ? dateSlotLine(order.pickup_date, order.pickup_slot, lang)
    : dateSlotLine(order.delivery_date, order.delivery_slot, lang);

  return (
    <IssueCard
      icon={pickup ? "car-outline" : "cube-outline"}
      eyebrow={t(pickup ? "home.issuePickupEyebrow" : "home.issueDeliveryEyebrow")}
      sub={slot}
      title={t(pickup ? "home.issuePickupTitle" : "home.issueDeliveryTitle", { name })}
      desc={t(pickup ? "home.issuePickupSubtitle" : "home.issueDeliverySubtitle")}
    >
      <button type="button" className="wosh-ord-issue__btn" onClick={() => onResolve(order, leg)}>
        <Icon name="calendar-outline" />
        {t("home.issueResolveBtn")}
      </button>
    </IssueCard>
  );
}

export function PaymentIssueCard({ order, info, t, onTryAnotherCard, onTopup }) {
  if (info.isCard) {
    return (
      <IssueCard
        icon="card-outline"
        eyebrow={t("orders.paymentCardEyebrow")}
        sub={info.cardLabel}
        title={t("orders.paymentCardFailedTitle")}
        desc={t("orders.paymentCardFailedSub")}
      >
        <button type="button" className="wosh-ord-issue__btn" onClick={onTryAnotherCard}>
          {t("orders.paymentTryAnotherCard")}
        </button>
        <button
          type="button"
          className="wosh-ord-issue__btn wosh-ord-issue__btn--ghost"
          onClick={() => onTopup(order, info)}
        >
          <Icon name="wallet-outline" />
          {t("orders.paymentUseWallet")}
        </button>
      </IssueCard>
    );
  }

  return (
    <IssueCard
      icon="wallet-outline"
      eyebrow={t("orders.paymentWalletEyebrow")}
      sub={t("orders.paymentWalletBalance", { amount: formatMoney(info.balance) })}
      title={t("orders.paymentWalletTitle")}
      desc={
        info.missing > 0
          ? t("orders.paymentWalletSub", { missing: formatMoney(info.missing) })
          : t("orders.paymentSubDeclined")
      }
    >
      <button type="button" className="wosh-ord-issue__btn" onClick={() => onTopup(order, info)}>
        {t("orders.paymentTopupWallet")}
      </button>
    </IssueCard>
  );
}
